// src/pages/business/StaffPage.tsx
// Exact replica of mobile BusinessStaffScreen.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserPlus, Users, AlertCircle, Loader2, RefreshCw, CheckCircle, Clock, XCircle } from 'lucide-react';
import apiClient from '@/services/apiClient';
import BusinessPageLayout from '@/components/business/BusinessPageLayout';
import { getImageUrl } from '@/config/media';
import { useToast } from '@/contexts/ToastContext';

interface StaffMember {
  id: string;
  name: string;
  role: string;
  phone?: string;
  status: string;
  verificationStatus?: string;
  avatar_url?: string;
}

// ── StaffCard — mirrors mobile StaffCard component ────────────────────────────
const StaffCard: React.FC<{
  member: StaffMember;
  onPress: () => void;
  onReview: () => void;
}> = ({ member, onPress, onReview }) => {
  const statusColor = member.status === 'active' ? '#10B981' : member.status === 'inactive' ? '#EF4444' : '#F59E0B';
  const verIcon = member.verificationStatus === 'verified'
    ? <CheckCircle size={14} style={{ color: '#10B981' }} />
    : member.verificationStatus === 'pending'
    ? <Clock size={14} style={{ color: '#F59E0B' }} />
    : <XCircle size={14} style={{ color: '#EF4444' }} />;

  return (
    <button
      onClick={onPress}
      className="w-full flex items-center gap-4 p-4 rounded-xl border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card shadow-sm hover:shadow-md transition-all text-left mb-3"
    >
      {/* Avatar */}
      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center bg-primary/10">
        {member.avatar_url
          ? <img src={getImageUrl(member.avatar_url)} alt={member.name} className="w-full h-full object-cover" />
          : <span className="text-primary font-bold text-lg">{member.name?.charAt(0).toUpperCase()}</span>
        }
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-light-text dark:text-dark-text truncate" style={{ fontSize: 16 }}>{member.name}</p>
        <p className="text-[14px] truncate" style={{ color: '#64748B' }}>{member.role}</p>
        {member.phone && <p className="text-[13px]" style={{ color: '#94A3B8' }}>{member.phone}</p>}
        <div className="flex items-center gap-2 mt-1">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }} />
          <span className="text-[12px] font-medium capitalize" style={{ color: statusColor }}>{member.status}</span>
          <span className="flex items-center gap-1 text-[12px]" style={{ color: '#64748B' }}>
            {verIcon}
            {member.verificationStatus || 'unverified'}
          </span>
        </div>
      </div>

      {/* Review button */}
      {member.verificationStatus === 'pending' && (
        <button
          onClick={e => { e.stopPropagation(); onReview(); }}
          className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white flex-shrink-0"
          style={{ backgroundColor: '#F59E0B' }}
        >
          Review
        </button>
      )}
    </button>
  );
};

// ── Screen ────────────────────────────────────────────────────────────────────
export const StaffPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStaff = useCallback(async () => {
    try {
      setError(null);
      const res = await apiClient.get(`/businesses/${businessId}/staff`);
      setStaff(res.data || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load staff members');
      toastError('Failed to load staff', err?.message || 'Could not fetch staff members.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [businessId]);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  const handleRefresh = () => { setRefreshing(true); loadStaff(); };

  const headerRight = (
    <button onClick={handleRefresh} disabled={refreshing} className="p-2">
      <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} style={{ color: '#4F46E5' }} />
    </button>
  );

  // Loading
  if (loading) {
    return (
      <BusinessPageLayout title="Business Staff">
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <Loader2 size={36} className="animate-spin" style={{ color: '#4F46E5' }} />
          {/* loadingText: fontSize 16 */}
          <p style={{ fontSize: 16, color: '#64748B' }}>Loading staff...</p>
        </div>
      </BusinessPageLayout>
    );
  }

  // Error
  if (error) {
    return (
      <BusinessPageLayout title="Business Staff">
        <div className="flex flex-col items-center justify-center py-24 gap-4 px-8 text-center">
          <AlertCircle size={64} style={{ color: '#EF4444' }} />
          <p style={{ fontSize: 16, color: '#0F172A' }}>{error}</p>
          <button onClick={loadStaff} className="px-6 py-3 rounded-xl text-white font-semibold" style={{ backgroundColor: '#4F46E5' }}>
            Retry
          </button>
        </div>
      </BusinessPageLayout>
    );
  }

  return (
    <BusinessPageLayout title="Business Staff" headerRight={headerRight}>

      {/* listContainer: padding 20 */}
      <div className="px-5 pt-4">
        {staff.length === 0 ? (
          // emptyContainer: center, paddingVertical 60
          <div className="flex flex-col items-center justify-center py-16 px-5 text-center gap-4">
            <Users size={64} style={{ color: '#94A3B8' }} />
            {/* emptyText: fontSize 16, fontWeight 600 */}
            <p className="font-semibold" style={{ fontSize: 16, color: '#94A3B8' }}>No staff members found for this business.</p>
            {/* emptySubtext: fontSize 14 */}
            <p style={{ fontSize: 14, color: '#94A3B8' }}>Tap the + button to add your first staff member.</p>
          </div>
        ) : (
          staff.map(member => (
            <StaffCard
              key={member.id}
              member={member}
              onPress={() => navigate(`/business/${businessId}/staff/${member.id}`)}
              onReview={() => navigate(`/business/${businessId}/staff/${member.id}/review`)}
            />
          ))
        )}
      </div>

      {/* FAB — bottom 30 right 30, w60 h60, borderRadius 30 */}
      <button
        onClick={() => navigate(`/business/${businessId}/staff/new`)}
        className="fixed flex items-center justify-center rounded-full shadow-xl z-50"
        style={{ bottom: 30, right: 30, width: 60, height: 60, backgroundColor: '#4F46E5' }}
      >
        <UserPlus size={24} color="#fff" />
      </button>
    </BusinessPageLayout>
  );
};

export default StaffPage;
